import { useLanguage } from "../context/LanguageContext";
import { PageHero, QuoteBand, PageCta } from "../components/PageHero";
import { Reveal, FadeIn } from "../components/motion";

export default function Leadership() {
  const { t } = useLanguage();
  const p = t.pages.leadership;
  return (
    <main data-testid="leadership-page">
      <PageHero testId="leadership-hero" label={p.label} title={p.title} sub={p.sub} />

      <section data-testid="leadership-why" className="bg-paper">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <div className="grid gap-14 md:grid-cols-12">
            <Reveal className="md:col-span-5">
              <h2 className="font-head text-3xl leading-tight text-ink sm:text-5xl">{p.whyTitle}</h2>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
              <p className="text-base font-light leading-relaxed text-ink/80 sm:text-lg">{p.whyText}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <QuoteBand
        testId="leadership-quote"
        image="/images/leadership-mountain.jpg"
        alt="Berggipfel im ersten Licht über weitläufiger Berglandschaft"
        line1={p.quote1}
        line2={p.quote2}
        position="center 30%"
      />

      <section data-testid="leadership-method" className="bg-[#F4F7F8]">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <Reveal y={14}>
            <p className="text-[13px] font-medium uppercase tracking-label text-wave">{p.methodLabel}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-head mt-8 max-w-2xl text-3xl leading-tight text-ink sm:text-5xl">{p.methodTitle}</h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden bg-ink/10 md:grid-cols-5">
            <FadeIn className="group bg-[#B8CBD6] p-10 transition-colors duration-700 sm:p-12 md:col-span-2">
              <span className="font-head text-6xl text-ink sm:text-7xl">20%</span>
              <h3 className="font-head mt-6 text-2xl text-ink">{p.methodInput}</h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-ink/75">{p.methodInputText}</p>
            </FadeIn>
            <FadeIn delay={0.15} className="group bg-deep p-10 transition-colors duration-700 sm:p-12 md:col-span-3">
              <span className="font-head text-6xl text-wave sm:text-7xl">80%</span>
              <h3 className="font-head mt-6 text-2xl text-white">{p.methodExplore}</h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-white/70">{p.methodExploreText}</p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section data-testid="leadership-fields" className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <Reveal y={14}>
            <p className="text-[13px] font-medium uppercase tracking-label text-wave">{p.fieldsLabel}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-head mt-8 max-w-3xl text-3xl leading-tight text-ink sm:text-5xl">{p.fieldsTitle}</h2>
          </Reveal>
          <div className="mt-16 grid gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-14">
            {p.fields.map((field, i) => (
              <Reveal key={field.title} delay={i * 0.1} className="group border-t border-ink/15 pt-8">
                <span className="font-head inline-block text-lg text-wave transition-transform duration-700 group-hover:translate-x-2">0{i + 1}</span>
                <h3 className="font-head mt-4 text-2xl leading-snug text-ink transition-colors duration-500 group-hover:text-deep sm:text-3xl">{field.title}</h3>
                {field.groups.map((group, gi) => (
                  <div key={group.sub} className={`mt-9 ${gi === 0 ? "lg:min-h-[216px]" : ""}`}>
                    <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-deep">{group.sub}</p>
                    <ul className="mt-4 space-y-2.5">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-sm font-light leading-relaxed text-ink/70">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-wave" aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PageCta title={p.ctaTitle} text={p.ctaText} />
    </main>
  );
}
